import { createFeatureSelector, createSelector, MemoizedSelector, MemoizedSelectorWithProps } from '@ngrx/store';
import { IUserStoreState } from './state';
import { FeatureName } from './feature';
import { createEntitySliceSelectors } from '@core-lib/store';
import { UserRole } from '@core-lib/enums';
import { UserPermission } from '@core-lib/enums/user-permission.enum';
import { ICarpaLight } from '@core-lib/models';

export const selectState: MemoizedSelector<object, IUserStoreState> = createFeatureSelector<IUserStoreState>(FeatureName);

export const {
	getEntity: getUser,
	selectEntity: selectUser,
	selectIsLoading: selectUserIsLoading,
	selectError: selectUserError
} = createEntitySliceSelectors(createSelector(selectState, state => state?.user));

export const {
	getEntity: getUsersTenant,
	selectEntity: selectUsersTenant,
	selectIsLoading: selectUsersTenantIsLoading,
	selectError: selectUsersTenantError
} = createEntitySliceSelectors(createSelector(selectState, state => state?.usersTenant));

const getUserPermissions = (state: IUserStoreState): UserPermission[] => findPermissions(state);
export const selectUserPermissions = createSelector(selectState, state => getUserPermissions(state));

export const selectUserInfoFullyLoaded = createSelector(selectState, (state): boolean => !!state?.user?.entity?.id);

const getUserHasPermission = (state: IUserStoreState, permision: UserPermission): boolean => {
	return getUserPermissions(state).includes(permision);
};

export const selectUserHasPermission: MemoizedSelectorWithProps<object, any, boolean> = createSelector(
	selectState,
	(state: IUserStoreState, props: { permission: UserPermission }) => getUserHasPermission(state, props.permission)
);

const getUserHasPermissions = (state: IUserStoreState, permissions: UserPermission[]): boolean => {
	return !permissions?.find(p => getUserPermissions(state)?.indexOf(p) === -1);
};

const getUserHasPermissionsAtLeast = (state: IUserStoreState, permissions: UserPermission[]): boolean => {
	return !!getUserPermissions(state).find(uP => permissions.includes(uP));
};

export const selectUserHasPermissions: MemoizedSelectorWithProps<object, any, boolean> = createSelector(
	selectState,
	(state: IUserStoreState, props: { permissions: UserPermission[] }) => getUserHasPermissions(state, props.permissions)
);

export const selectUserHasPermissionsAtLeast: MemoizedSelectorWithProps<object, any, boolean> = createSelector(
	selectState,
	(state: IUserStoreState, props: { permissions: UserPermission[] }) => getUserHasPermissionsAtLeast(state, props.permissions)
);

export const selectUserRoles = createSelector(selectState, state => state?.user?.entity?.roles);

const getUserHasRole = (state: IUserStoreState, role: UserRole): boolean => {
	return state?.user?.entity?.roles?.some(r => r == role);
};

export const selectUserHasRole: MemoizedSelectorWithProps<object, any, boolean> = createSelector(
	selectState,
	(state: IUserStoreState, props: { role: UserRole }) => getUserHasRole(state, props.role)
);

function findPermissions(state: IUserStoreState): UserPermission[] {
	return state.user?.entity?.permissions ?? [];
}

export const {
	getEntity: getCarpas,
	selectEntity: selectCarpas,
	selectIsLoading: selectCarpasIsLoading,
	selectError: selectCarpasError
} = createEntitySliceSelectors(createSelector(selectState, state => state?.carpas));

export const {
	selectEntity: selectCurrentCarpa,
	selectIsLoading: selectCurrentCarpaIsLoading,
	selectError: selectCurrentCarpaError
} = createEntitySliceSelectors(createSelector(selectState, state => state?.currentCarpa));

export const selectUserTenants = createSelector(selectState, (state): ICarpaLight[] => state?.user?.entity?.tenants ?? []);
export const selectUserHasMultipleTenants = createSelector(selectState, (state): boolean => state?.user?.entity?.tenants.length > 1);
